import * as React from 'react'
import { observer } from 'mobx-react'
import Icon from 'components/Icon'
import {
  TableHeader,
  TableCell,
  TableHost,
  TableEmptyStateContainer,
  ImageCell,
  DateCell,
} from 'components/Table'
import {
  TableSearchbar,
  TableSearchbarMenuItem,
} from 'payouts/components/TableSearchbar'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import Checkbox from 'components/Checkbox'
import {
  PayoutReviewViewModel,
  SelectionState,
} from 'payouts/admin/review/PayoutReviewViewModel'
import { match } from 'utils/pattern-match-util'
import {
  Dropdown,
  DropdownTitle,
  DropdownItem,
  DropdownItemTitle,
} from 'components/Dropdown'
import { IconButton } from 'components/IconButton'
import Button from 'components/Button'
import {
  ContextCell,
  AmountCell,
  IdentifierCell,
  FlagsCell,
} from 'payouts/components/PayoutTransactionsTableCells'
import { TransactionSourceType } from 'payouts/TransactionSourceType'
import { PendingTransactionViewModel } from '../../../PendingTransactionViewModel'
import { useToggle } from 'hooks/state-hooks'
import FilterControlsToggleButton from 'components/FilterControlsToggleButton'
import FilterControls from 'infra/FilterControls'
import { expr } from 'mobx-utils'
import VirtualTable from 'payouts/components/VirtualTable'
import { useTaskGroup } from 'hooks/mobx-hooks'

interface PendingTransactionsTableProps {
  vm: PayoutReviewViewModel
}

/**
 * The PendingTransactionsTable
 */
export default observer(function PendingTransactionsTable({
  vm,
}: PendingTransactionsTableProps) {
  /*
    Since the draft is used to filter out already approved
    transactions from the table, we want to wait for both
    to have been fetched before displaying the pending transactions
    to avoid flashing.
   */
  const isLoading =
    vm.pendingTransactionsScroller.fetch.pending ||
    vm.draftingVM.fetchCurrentDraft.pending

  const shouldShowEmptyState = expr(
    () =>
      vm.pendingTransactions.length === 0 &&
      vm.pendingTransactionsScroller.isDone,
  )

  const filtersShowing = useToggle(false)
  const approveTasks = useTaskGroup([
    vm.approveSelectedTransactions,
    vm.approveTransactionsMatchingFilter,
  ])

  return (
    <>
      <TableHost>
        <TableSearchbar
          disabled={vm.actionsDisabled}
          searchText={vm.searchText.value}
          onSearchTextChange={vm.searchText.change}
        >
          <TableSearchbarMenuItem>
            <FilterControlsToggleButton
              active={filtersShowing.isEnabled}
              count={vm.filters.appliedFilterCount}
              onClick={filtersShowing.toggle}
            />
          </TableSearchbarMenuItem>
          <TableSearchbarMenuItem>
            <Dropdown
              autoDismiss
              disabled={vm.actionsDisabled || approveTasks.pending}
              button={
                <IconButton
                  loading={approveTasks.pending}
                  disabled={vm.actionsDisabled}
                >
                  <Icon name="more" />
                </IconButton>
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                icon={<Icon name="add" />}
                disabled={vm.selected.size === 0}
                onClick={vm.approveSelectedTransactions}
              >
                <DropdownItemTitle>
                  Add {vm.selected.size || ''} transactions to payout
                </DropdownItemTitle>
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="add" />}
                disabled={
                  vm.pendingTransactions.length === 0 || vm.selected.size > 0
                }
                onClick={vm.approveTransactionsMatchingFilter}
              >
                <DropdownItemTitle>
                  Add transactions matching filter to payout
                </DropdownItemTitle>
              </DropdownItem>
              <DropdownItem
                disabled={vm.exportCsv.pending}
                icon={<Icon name="download" />}
                onClick={vm.exportCsv}
              >
                <DropdownItemTitle>Download CSV</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </TableSearchbarMenuItem>
        </TableSearchbar>
        <FilterControls vm={vm.filters} active={filtersShowing.isEnabled} />

        {isLoading || !shouldShowEmptyState ? (
          <VirtualTable
            totalCount={vm.pendingTransactions.length}
            overscan={400}
            scroller={vm.pendingTransactionsScroller}
            itemContent={(index) => {
              const tvm = vm.pendingTransactions[index]
              return (
                <TransactionRowContent
                  vm={vm}
                  key={tvm.transaction.id}
                  tvm={tvm}
                />
              )
            }}
            headerContent={() => (
              <>
                <TableHeader align="left">
                  <Checkbox
                    onChange={vm.toggleListSelection}
                    disabled={vm.actionsDisabled}
                    checked={match(vm.selectionState, {
                      [SelectionState.AllSelected]: () => true,
                      [SelectionState.NoneSelected]: () => false,
                      [SelectionState.SomeSelected]: () =>
                        'indeterminate' as boolean | 'indeterminate',
                    })}
                  />
                </TableHeader>
                <TableHeader align="left">Context</TableHeader>
                <TableHeader align="left">Recipient</TableHeader>
                <TableHeader align="right">Amount</TableHeader>
                <TableHeader align="left">Workspace</TableHeader>
                <TableHeader align="left">ID</TableHeader>
                <TableHeader align="left">Created</TableHeader>
                <TableHeader align="left">Transacted</TableHeader>
                <TableHeader align="left">Iteration</TableHeader>
                <TableHeader align="left">Flags</TableHeader>
                <TableHeader align="right"></TableHeader>
              </>
            )}
          />
        ) : (
          <PendingTransactionsEmptyState
            appliedFilters={vm.filtersApplied}
            onClearFilters={vm.clearFilters}
          />
        )}
      </TableHost>
    </>
  )
})

interface TransactionRowContentProps {
  vm: PayoutReviewViewModel
  tvm: PendingTransactionViewModel
}

/**
 * Transaction Row
 */
const TransactionRowContent = observer(function TransactionRow({
  vm,
  tvm,
}: TransactionRowContentProps) {
  return (
    <>
      <TableCell width={64}>
        <Checkbox
          disabled={vm.actionsDisabled}
          checked={tvm.selected}
          onChange={tvm.toggleSelection}
        />
      </TableCell>
      <ContextCell
        source={tvm.transaction.source}
        userTransactionMemo={tvm.transaction.memo}
        userTransactionReason={tvm.transaction.reason}
        jobTransactionJobId={tvm.transaction.jobId}
        lastFailureDescription={tvm.transaction.lastFailureDescription}
      />
      <ImageCell
        title={tvm.transaction.recipient.displayName}
        image={tvm.transaction.recipient.avatar}
        imageFallback={tvm.transaction.recipient.avatarFallback}
        minWidth={110}
      />
      <AmountCell amount={tvm.transaction.amount} />
      <ImageCell
        title={tvm.transaction.workspace.name}
        image={tvm.transaction.workspace.branding.logos.icon}
        minWidth={110}
      />
      <IdentifierCell value={tvm.transaction.shortId} />
      <DateCell
        date={tvm.transaction.createdAt}
        dataTest={`pending-transaction-date-created-${tvm.transaction.id}`}
      />
      <DateCell
        date={tvm.transaction.transactedAt}
        dataTest={`pending-transaction-date-transacted-${tvm.transaction.id}`}
      />
      <TableCell>{tvm.transaction.iteration}</TableCell>
      <FlagsCell flags={tvm.transaction.flags} />
      <TableCell align="right">
        <Dropdown
          autoDismiss
          disabled={vm.actionsDisabled}
          button={
            // Needs to be inline, otherwise it doesn't align
            // correctly with the `more` button in the searchbar.
            <IconButton
              inline
              loading={tvm.tasks.pending}
              disabled={tvm.tasks.pending || vm.actionsDisabled}
            >
              <Icon name="more" />
            </IconButton>
          }
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownItem onClick={tvm.approve} icon={<Icon name="add" />}>
            Add to payout
          </DropdownItem>
          {tvm.transaction.source === TransactionSourceType.User && (
            <DropdownItem
              onClick={tvm.requestCancellation}
              icon={<Icon name="close" />}
            >
              Cancel
            </DropdownItem>
          )}
          {tvm.transaction.jobId && (
            <>
              <DropdownItem
                onClick={tvm.viewJobInHQ}
                icon={<Icon name="view" />}
              >
                View job in HQ
              </DropdownItem>
              <DropdownItem onClick={tvm.viewJob} icon={<Icon name="view" />}>
                View job in Work Portal
              </DropdownItem>
            </>
          )}
        </Dropdown>
      </TableCell>
    </>
  )
})

/**
 * Empty state.
 */
function PendingTransactionsEmptyState({
  appliedFilters,
  onClearFilters,
}: {
  appliedFilters: boolean
  onClearFilters: () => void
}) {
  return (
    <TableEmptyStateContainer bordered>
      {appliedFilters ? (
        <EmptyState
          title="We couldn't find what you were looking for."
          subtitle={
            <>
              Maybe try{' '}
              <Button size="baseline" type="text" onClick={onClearFilters}>
                clearing your search filter
              </Button>
              ?
            </>
          }
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_search-noresults.svg')}
            />
          }
        />
      ) : (
        <EmptyState
          title="No transactions left to approve."
          subtitle="Pending transactions will appear here."
          small
          image={
            <EmptyStateIllustrationImage
              src={require('assets/images/img_job-completed.svg')}
            />
          }
        />
      )}
    </TableEmptyStateContainer>
  )
}
